import { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import { SiWhatsapp } from 'react-icons/si';
import { ROUTE_PATHS } from '@/lib/index';
import { springPresets, fadeInUp } from '@/lib/motion';
export default function Layout() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
    };
    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    to: ROUTE_PATHS.HOME,
    label: 'الرئيسية'
  }, {
    to: ROUTE_PATHS.CORPORATE_CATERING,
    label: 'كيترنج الشركات'
  }, {
    to: ROUTE_PATHS.WEDDINGS_EVENTS,
    label: 'أعراس ومناسبات'
  }, {
    to: ROUTE_PATHS.CAFE_SUPPLY,
    label: 'توريد الكافيهات'
  }, {
    to: ROUTE_PATHS.BAKERY_DESSERTS,
    label: 'مخبوزات وحلويات'
  }, {
    to: ROUTE_PATHS.ABOUT,
    label: 'من نحن'
  }, {
    to: ROUTE_PATHS.CONTACT,
    label: 'تواصل معنا'
  }];
  return <div className="min-h-screen" dir="rtl">
      <motion.header ref={headerRef} initial={{
      backgroundColor: 'rgba(247, 241, 232, 0)'
    }} animate={{
      backgroundColor: isScrolled ? 'rgba(247, 241, 232, 0.95)' : 'rgba(247, 241, 232, 0)',
      boxShadow: isScrolled ? '0 4px 12px rgba(75, 46, 34, 0.08)' : '0 0 0 rgba(0,0,0,0)'
    }} transition={springPresets.gentle} className="fixed top-0 right-0 left-0 z-50 backdrop-blur-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <NavLink to={ROUTE_PATHS.HOME} className="flex items-center gap-2">
              <motion.div whileHover={{
              scale: 1.05
            }} transition={springPresets.snappy} className="text-2xl font-bold">
                <span className="text-primary font-['Cairo']" style={{
                fontFamily: 'Cairo, sans-serif'
              }}>أطياف</span>
                <span className="text-accent mx-1">•</span>
                <span className="text-primary font-['Cairo']" style={{
                fontFamily: 'Cairo, sans-serif'
              }}>الخبازين</span>
              </motion.div>
            </NavLink>

            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map(link => <NavLink key={link.to} to={link.to} className={({
              isActive
            }) => `text-sm font-medium transition-all duration-300 hover:text-accent relative ${isActive ? 'text-accent' : 'text-foreground'}`} style={{
              fontFamily: 'Cairo, sans-serif'
            }}>
                  {({
                isActive
              }) => <>
                      {link.label}
                      {isActive && <motion.div layoutId="activeNav" className="absolute -bottom-1 right-0 left-0 h-0.5 bg-accent" transition={springPresets.snappy} />}
                    </>}
                </NavLink>)}
            </nav>

            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-2 text-foreground hover:text-accent transition-colors" aria-label="Toggle menu">
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={springPresets.gentle} className="lg:hidden bg-background border-t border-border">
              <nav className="container mx-auto px-4 py-6 flex flex-col gap-4">
                {navLinks.map(link => <NavLink key={link.to} to={link.to} onClick={() => setIsMobileMenuOpen(false)} className={({
              isActive
            }) => `text-base font-medium py-2 px-4 rounded-lg transition-all ${isActive ? 'bg-accent/10 text-accent' : 'text-foreground hover:bg-muted'}`} style={{
              fontFamily: 'Cairo, sans-serif'
            }}>
                    {link.label}
                  </NavLink>)}
              </nav>
            </motion.div>}
        </AnimatePresence>
      </motion.header>

      <main style={{
      paddingTop: `${headerHeight}px`
    }}>
        <Outlet />
      </main>

      <motion.a href="https://wa.me/966550096297" target="_blank" rel="noopener noreferrer" className="fixed bottom-8 left-8 z-40 bg-[#25D366] text-white p-4 rounded-full shadow-lg" whileHover={{
      scale: 1.1,
      boxShadow: '0 8px 24px rgba(37, 211, 102, 0.4)'
    }} whileTap={{
      scale: 0.95
    }} transition={springPresets.snappy} aria-label="WhatsApp">
        <SiWhatsapp size={28} />
      </motion.a>

      <footer className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            <div>
              <div className="text-2xl font-bold mb-4">
                <span className="text-primary-foreground font-['Cairo']" style={{
                fontFamily: 'Cairo, sans-serif'
              }}>أطياف</span>
                <span className="text-accent mx-1">•</span>
                <span className="text-primary-foreground font-['Cairo']" style={{
                fontFamily: 'Cairo, sans-serif'
              }}>الخبازين</span>
              </div>
              <p className="text-primary-foreground/80 text-sm leading-relaxed" style={{
              fontFamily: 'Cairo, sans-serif'
            }}>
                معمل متخصص في الكيترنج والمخبوزات والحلويات للشركات والمناسبات والكافيهات
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4 text-accent" style={{
              fontFamily: 'Cairo, sans-serif'
            }}>روابط سريعة</h3>
              <ul className="space-y-2">
                {navLinks.slice(0, 4).map(link => <li key={link.to}>
                    <NavLink to={link.to} className="text-primary-foreground/80 hover:text-accent transition-colors text-sm" style={{
                  fontFamily: 'Cairo, sans-serif'
                }}>
                      {link.label}
                    </NavLink>
                  </li>)}
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4 text-accent" style={{
              fontFamily: 'Cairo, sans-serif'
            }}>خدماتنا</h3>
              <ul className="space-y-2">
                {navLinks.slice(4).map(link => <li key={link.to}>
                    <NavLink to={link.to} className="text-primary-foreground/80 hover:text-accent transition-colors text-sm" style={{
                  fontFamily: 'Cairo, sans-serif'
                }}>
                      {link.label}
                    </NavLink>
                  </li>)}
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4 text-accent" style={{
              fontFamily: 'Cairo, sans-serif'
            }}>تواصل معنا</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-primary-foreground/80 text-sm">
                  <Phone size={16} className="text-accent" />
                  <span style={{
                  fontFamily: 'Cairo, sans-serif'
                }}>0550096297</span>
                </li>
                <li className="flex items-center gap-2 text-primary-foreground/80 text-sm">
                  <Mail size={16} className="text-accent" />
                  <span>info@atyafalkhabbazin.sa</span>
                </li>
                <li className="flex items-start gap-2 text-primary-foreground/80 text-sm">
                  <MapPin size={16} className="text-accent mt-1 flex-shrink-0" />
                  <span style={{
                  fontFamily: 'Cairo, sans-serif'
                }}>الرياض، المملكة العربية السعودية</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-primary-foreground/20 mt-12 pt-8 text-center">
            <p className="text-primary-foreground/60 text-sm" style={{
            fontFamily: 'Cairo, sans-serif'
          }}>
              © 2026 أطياف الخبازين. جميع الحقوق محفوظة.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}